/**
 * `<CombatStage>` — COMBAT.app's tabletop, rendered on a single GPU-backed WebGL canvas via **PixiJS**
 * (`pixiCombatBoard.ts`'s `mountCombatBoard`). One pan/zoom-able world container composes the board: a
 * background lattice container, a **terrain/edge-wall container**, and a **token container**
 * on top. Tile textures and a battlemap underlay could grow as further containers on this same world
 * root (no fog-of-war render surface exists today — fog-of-war is a visibility/concealment state
 * feature, not a canvas layer).
 *
 * **Board chrome.** The board sits on the GMS//OS brutalist stage (`brutalist-tokens.css`): a
 * near-black stage, a crisp bone frame on a hard ink shadow, and a muted JetBrains Mono **coordinate
 * ruler** (column letters above, row numbers left). The grid itself is the terrain art's corner
 * crosses. Pure render — no model change.
 *
 * **Terrain authoring — two gestures:** `EncounterState.terrain`
 * (whole-cell fills) and `edges` (boundary walls) render in their own non-interactive container (never
 * captures a pointer event), read straight from state. Two separate authoring tools write them, each a left-drag committed as ONE
 * atomic `batch` (one undo entry), with all decisions in the pure, unit-tested {@link combatAuthoring}:
 *   - **Tile brushes** (solid/cover/erase, the TERRAIN rail) paint the **cell body only** as a
 *     rubber-band **box** by default ({@link combatAuthoring} `tileBoxCommand`): `onMouseDown` fixes the start cell + the place-vs-clear direction (or a clear
 *     when **Alt** is held — the erase modifier), `onMouseMove` sizes the box to the cursor cell, and
 *     release fills the clamped rectangle with `set_terrain` children (no-op + off-board cells
 *     dropped). A click is a one-cell box; a box never skips a cell on a fast drag. Holding **Shift**
 *     at press instead opens the **freehand stroke** ({@link combatAuthoring} `tileStrokeCommand`,
 *     for organic shapes a box can't make) — same direction-at-press, same one-batch-one-undo grain.
 *   - The **wall tool** (`tool === "wall"`, the WALLS rail) lays edge walls as a **rubber-band run**
 *     ({@link combatAuthoring} `wallRunCommand`): `onMouseDown` snaps the press to a grid **vertex**
 *     ({@link vertexAt}), `onMouseMove` rubber-bands a dashed preview (its own non-interactive container,
 *     {@link wallRunEndpoint}) along the dominant axis to the vertex under the cursor, and release
 *     commits the straight run of `set_edge` children — direction (set vs clear) fixed by the first
 *     edge under the press vertex (so the one tool both walls and clears, no separate erase mode);
 *     no-op edges dropped, fully-off-board edges dropped, a border wall (one side off-board) kept.
 * Both close on `onMouseUp` (and a shared window-level `mouseup` safety net for an off-canvas release).
 * Token click/drag hit-testing is skipped while either tool is active so the whole board (even under a token) is
 * authorable, and left-drag pan is off under them so the drag authors rather than pans. Authoring is
 * GM-gated by the same `disabled` thread the GRID/UNITS tools use (the brush is `null` / the wall tool
 * downgrades to select when the GM can't author); the server gate stays the real wall.
 *
 * **Uncommitted authoring previews.** Every armed gesture shows what a
 * release will do *before* it commits, including its direction (add vs remove), each in its own
 * non-interactive container (a pure affordance — never captures a pointer event, only rendered while
 * the brush/tool is armed, so the GM gate already gates it), drawn by `pixiCombatBoard.ts`'s
 * `setAuthoringPreview` (its `drawCellsPreview`/`drawBoxPreview`/`drawLinePreview` helpers):
 *   - **Tile drag ghost** (`drawBoxPreview`/`drawCellsPreview`): while a stroke is open, a live overlay of the cells
 *     {@link strokeCells} will paint. Sampling stays authoritative in `strokeRef` (no re-render);
 *     `tilePreview` state is derived from it and re-renders only when the covered-cell SET grows (a
 *     new cell entered), so a fast drag doesn't thrash React. An **add** ghosts the target fill at
 *     reduced opacity; a **remove** reads as a red removal wash — the {@link strokeDirection} of the
 *     press-fixed {@link strokeTarget}, so the ghost matches the commit exactly.
 *   - **Tile hover hint** (`drawBoxPreview`, one cell): with a tile brush armed and no stroke open,
 *     the cell under the cursor highlights with the same add-vs-remove read, resolved against THAT
 *     cell's flags (the hovered cell is the would-be press cell). Cleared on `onMouseLeave`.
 *   - **Wall direction cue** (`drawLinePreview`): the rubber-band run's line is gold when it will
 *     SET walls, the removal red when it will CLEAR — {@link wallRunDirection}, the same first-edge
 *     toggle {@link wallRunCommand} commits.
 *
 * **Why Pixi/WebGL, not SVG/three:** the combat map is a multi-container raster+vector surface
 * (battlemap → textures → walls → tokens), and Pixi's retained GPU scene graph composites all of it as
 * one canvas, redrawing on the GPU only what actually moved rather than re-rasterising a whole layer on
 * the CPU for any change; the 3D hex grid (`lib/hexMap/`) is a different projection on its own
 * three.js stack and is not reused here.
 *
 * **Pan/zoom model (tool-modal):** the view transform (`{x, y, scale}`)
 * is React state, applied to the Pixi world container (`pixiCombatBoard.ts`'s `setView`). Panning is
 * **decoupled from the plain left button** so the left button is free for the armed tool
 * (select/move, or paint). Pan fires three ways, all writing the new position back to state via the
 * native-DOM `beginPan`/`onPanMove`/`onPanEnd` handlers (window-level while a pan is in progress):
 *   - **middle-mouse drag** — always, in any tool (armed manually on `onMouseDown`; a native-DOM
 *     middle-press only ever calls `beginPan`, so it can never accidentally grab a token, and no
 *     button-narrowing config is needed);
 *   - **right-mouse drag** — likewise always, in any tool, and through the SAME `beginPan` (the
 *     board has no other right-button gesture and no context menu of its own, so the button is free
 *     for the hand every VTT user reaches for). The one cost is that the browser's native menu is
 *     suppressed over the Pixi host — `contextmenu` is `preventDefault`ed on that element only, so
 *     right-click is untouched everywhere else in the app. A STATIONARY right-click over the host
 *     disarms the board's armed thing (layer 3, `decisions/0189`) — see `RIGHT_CLICK_SLOP_PX`;
 *   - **space-held + left-drag** — a transient hand, in any tool (the `spaceHeld` window listener);
 *   - the **rail "Pan" tool** (`tool === "pan"`) — a sticky hand-drag on the left button.
 * Concretely {@link combatPan} `startsPan` owns the button rule — left-drag only arms `beginPan`
 * when `canPanLeft` (`tool === "pan" || spaceHeld`), while middle- and right-drag ride the manual
 * path on top regardless of tool. Zoom is wheel-driven and **anchored to
 * the cursor** — the world point under the pointer stays fixed across the scale change. Grid lines
 * render at a fixed world-space stroke width (`pixiCombatBoard.ts`'s `buildBoardContainer`).
 *
 * **Tokens:** each *placed* combatant (anchor `pos != null`) draws as
 * a footprint-sized token in the token container; unplaced combatants are not drawn. Anchors are read as
 * `Cell` and narrowed to the square arm at the render fence ({@link combatCells} `asSquareCell`) — the
 * surface is square-only; a hex cell here is a bug. Interactions issue `move_token` through the live
 * encounter room: dragging a token snaps to a cell on drop, and **the token's resting position is
 * driven by encounter state, not local drag** — an illegal drop (off-map / footprint spill / occupied)
 * is rejected by the server-authoritative reducer (the same wall the local optimistic apply runs) and
 * the node snaps back. Clicking an empty cell while a unit is armed for placement first-places it.
 * Control is mirrored as an **affordance** (a token is draggable only when the actor controls it); the
 * server gate (`canIssueEncounter`) stays the real wall and is never re-enforced here.
 */
import { faEraser } from "@fortawesome/free-solid-svg-icons";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { roomContainsCell } from "@lancer-vtt/domain";
import type { Cell, Combatant, MovePathPreview, OptInActionRiderPrompt } from "@lancer-vtt/domain";
import { squareKey } from "@lancer-vtt/space";
import { cellPickBypassesSelect } from "../../../lib/encounter/combatInspector";
import type { ThreatRingsView } from "../../../lib/encounter/hoverIntel";
import { CELL, anchorFromMinCorner, asSquareCell, footprintBox, hitTestToken, pickToken } from "../combatCells";
import type { SquareCell } from "@lancer-vtt/domain";
import { cellAt, storedCellAt, strokeCells, strokeTarget, vertexAt } from "../combatAuthoring";
import { opensPan, panNeedsPreventDefault } from "../combatPan";
import { clampView } from "../combatView";
import { mountCombatBoard, type CombatBoardHandle } from "../pixiCombatBoard";
import { objectPlacementCommand } from "../objectAuthoring";
import {
  accumulatePolylineVertex,
  ERASER_HIT_RADIUS,
  hitTestAnnotation,
  removeAnnotationCommand,
  shouldSampleFreehandPoint,
} from "../combatDrawing";
import { AnnotationInspector } from "./AnnotationInspector";
import { GhostDragCallout, placeGhostCallout, type CalloutAnchor } from "./GhostDragCallout";
import { InspectReadoutFrame } from "./InspectReadoutFrame";
import { calloutContent, dragKindFor, dropOutcome, streamsMovePreview, trailRefused, trampledIds, type DragKind, type ForecastRow } from "./tokenDragPlan";
import { buildBoardDebugHook } from "./boardDebug";
import type { MeasureDebug } from "../combatApp/useMeasureTool";
import { isMeasureDrag, measureEndFor, type MeasureEnd } from "../combatApp/measureTool";
import { useMeasureTouch } from "./useMeasureTouch";
import { MAX_SCALE, MIN_SCALE, PING_HOLD_MS, PING_MOVE_TOLERANCE, POLYLINE_DBLCLICK_FINISH_DIST_SQ, RIGHT_CLICK_SLOP_PX, ZOOM_STEP } from "./constants";
import { useEscEntry } from "../../../lib/encounter/escLayers";
import type { CombatStageProps } from "./props";
import { isTextTarget } from "./textTarget";
import type { View } from "./types";
import { useAnnotationSelection } from "./useAnnotationSelection";
import { useAttackHover } from "./useAttackHover";
import { useAuthoringGestures } from "./useAuthoringGestures";
import { useAuthoringPreviewSync } from "./useAuthoringPreviewSync";
import { useBoardSync } from "./useBoardSync";
import { useCursorStream } from "./useCursorStream";
import { useDrawGestures, visibleAnnotations } from "./useDrawGestures";

/**
 * The eraser's cursor: Font Awesome's eraser, white with a dark outline so it reads on any tile, with
 * its hotspot at the bottom-left rubber end, which sits at the centre of the eraser's ring.
 */
/** A drag over an illegal drop cell draws no rings. */
const NO_RINGS: ThreatRingsView = { rings: [], cover: new Map() };
/** How long the pointer rests on a drag cell before a slow unit's plain rings snap to cover-aware. */
const DRAG_RINGS_REST_MS = 100;

const ERASER_CURSOR = (() => {
  const [w, h, , , path] = faEraser.icon;
  const d = typeof path === "string" ? path : path.join(" ");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="-32 -32 ${w + 64} ${h + 64}"><path d="${d}" fill="#fff" stroke="#111" stroke-width="36"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 3 18, crosshair`;
})();
import { useMovePreviewStream } from "./useMovePreviewStream";
import { usePingHold } from "./usePingHold";
import { useSpaceHeld } from "./useSpaceHeld";
import { useStagePan } from "./useStagePan";
import { useStage } from "../shell/stageContext";
import { BoostDropCard } from "./BoostDropCard";

/** An accepted PLAY drop, ready to send: where to, its chosen route, and where its ghost holds. */
type PlayDrop = { id: string; dest: SquareCell; path: Cell[] | null; ghostAt: { x: number; y: number }; dragToken: number };
/** A PLAY drop held on the Boost card, with the riders the card asks about. */
type BoostDrop = PlayDrop & { riders: OptInActionRiderPrompt[] };

export function CombatStage({
  cols,
  rows,
  combatants,
  tokenImageCtx,
  tokenChrome,
  terrain,
  edges,
  ground,
  rooms,
  sitrepZones,
  areaEffects,
  annotations,
  drawTool,
  esc,
  drawColor,
  fullGrid,
  terrainMechanics,
  brush,
  tool,
  coverGrade,
  hazard,
  tile,
  wallHeight,
  wallCoverGrade,
  eraseMode,
  autoWall,
  objectPlan,
  selectedId,
  reachable,
  sensorRange,
  boostRange,
  threatRings,
  threatRingsAt,
  pathPreview,
  trampleTrail,
  isPlanningMove,
  moveForecast,
  boostDropRiders,
  activeUnitName,
  registerNode,
  unregisterNode,
  getNode,
  setTicker,
  onLocalMove,
  watchSettle,
  noteMoveHeld,
  moveRejectionSeq,
  undoRedoSeq,
  floats,
  onFloatDone,
  projectiles,
  onProjectileDone,
  impacts,
  onImpactDone,
  chainFocus,
  isAnimating,
  onSkipAnimations,
  attackRange,
  outOfRangeTargetIds,
  attackAffected,
  attackLosExcluded,
  attackArea,
  cellPickArmed,
  cellSightlines,
  pendingDeploy,
  targetPicking,
  onCommitCell,
  directionPicking,
  onCommitDirection,
  onSelect,
  onInspect,
  onInspectRoom,
  dispatch,
  actorId,
  isGm,
  canControl,
  canPlayMove,
  placing,
  moveCommandType,
  onPing,
  ping,
  focusPoint,
  onCursor,
  onHoverCell,
  cursors,
  slotNameOf,
  onMovePreview,
  movePreviews,
  overlay,
  onTokenHover,
  targetRings,
  targetBadges,
  expandedTargetId,
  measureArmed = false,
  onMeasurePointer,
  measureRender,
  measureDebugRef,
  inspectArmed = false,
  inspect,
}: CombatStageProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [view, setViewRaw] = useState<View>({ x: 0, y: 0, scale: 1 });

  // Center the board on first measure only — never fight the user's pan/zoom afterward.
  const centered = useRef(false);

  // The live move-path highlight (the "tiles this drag travels through" wash): the cell-key set the
  // dragged token's footprint sweeps to its current hovered anchor, recomputed (via `pathPreview`)
  // only when the drag crosses into a new cell, and cleared on drop. `null` when not dragging or the
  // hovered drop is out of range / illegal (nothing to commit ⇒ no trail). Local to this surface.
  const [movePath, setMovePath] = useState<MovePathPreview | null>(null);
  // A Trampler's drag (Q3): its trail is one the drop would refuse (drawn red), and the characters
  // it would trample (outlined). `null` rings outside such a drag, so the command menu's own show.
  const [movePathRefused, setMovePathRefused] = useState(false);
  const [trampleRingIds, setTrampleRingIds] = useState<string[] | null>(null);
  // The Boost ring shows only while a PLAY drag's anchor is past the unit's base move reach (R5),
  // so the player sees the limit they are pushing past; hidden at selection and after the drop.
  const [dragPastReach, setDragPastReach] = useState(false);
  // An open PLAY drag's rings (`projects/intel-follows-selection.md`): the dragged unit's weapon
  // rings measured from the drop cell, empty over an illegal one (the trail's own rule). Non-null
  // exactly while the ghost is out. The move wash stays: it is where a drop is legal.
  const [dragRings, setDragRings] = useState<ThreatRingsView | null>(null);
  // A PLAY drop whose folded Boost carries opt-in riders (`move-family-opt-in-surface` Phase 1): the
  // ghost holds at the drop cell and the Boost card asks before anything is sent. `null` otherwise.
  const [boostDrop, setBoostDrop] = useState<BoostDrop | null>(null);
  const {
    attackHover,
    setAttackHover,
    attackHoverCellRef,
    attackAffectedRef,
    attackLosExcludedHover,
    setAttackLosExcludedHover,
    attackLosExcludedRef,
    attackRangeRef,
    reachableRef,
    cellSightlinesRef,
  } = useAttackHover({ attackRange, reachable, cellSightlines });

  // ── Map ping (this feature) ──────────────────────────────────────────────────────────────
  // A left-press held STILL for `PING_HOLD_MS` drops a ping everyone sees — over empty board OR
  // over a token (a token press bubbles to the stage; see `Token`). There is NO charging visual
  // (it read as distracting): the hold runs on a silent timer, and the received burst (your own
  // ping echoes back) is the feedback. `pingPressRef` holds the in-flight press — the screen px
  // (to measure the move-tolerance cancel), the world point to ping, and the pending fire timer.
  // A pointer travel past the tolerance, the start of any drag, or an early release cancels it.
  const { pingPressRef, justPingedRef, tokenPressRef, cancelPing, firePing } = usePingHold(onPing);
  // A lone press on the selected token where others share the point (a rider on its carrier): the
  // unit the trailing click steps the selection to, if the pointer stays put (`pickToken`'s cycle).
  // The press itself keeps the selection, so a drag of the selected carrier still drags the carrier.
  const cyclePressRef = useRef<{ screenX: number; screenY: number; nextId: string } | null>(null);

  // ── Live cursors (this feature) ────────────────────────────────────────────────────────────
  // Stream our pointer over the board to everyone else, throttled. `onCursor` is read through a
  // ref so the throttled sender stays stable; `lastCursorRef` timestamps the last emit (reset to 0
  // on a clear, so the next entry sends immediately). Received cursors render via `pixiCombatBoard.ts`'s
  // `setCursors`.
  const streamCursor = useCursorStream(onCursor);

  // The cell last reported through `onHoverCell`, as a key — the coalescing gate. Pointer-move fires
  // per pixel; the Map key's hovered tile only ever changes when the CELL changes, so reporting per-pixel would
  // re-render the pane dozens of times per cell crossed for no observable difference. A ref, not
  // state, so the compare itself never re-renders this component.
  const hoverReportRef = useRef<string | null>(null);
  const reportHoverCell = useCallback(
    (cell: SquareCell | null) => {
      const key = cell ? `${cell.x},${cell.y}` : null;
      if (key === hoverReportRef.current) return;
      hoverReportRef.current = key;
      onHoverCell?.(cell);
    },
    [onHoverCell],
  );

  /** The token the pointer was last reported to be over, as an id — the coalescing gate for the
   *  board's raised label layer (`pixiCombatBoard.ts`'s `setHoveredToken`/`syncLabelReveal`, which
   *  draws a hovered unit's name above everything else and un-hides one the solver had no room for).
   *  Same shape and same reason as {@link hoverReportRef} above: pointer-move fires per pixel and the
   *  reveal only ever changes when the token under the cursor does. A ref, not state, so neither the
   *  compare nor the report re-renders this component — the reveal is a pure display-list move
   *  inside the renderer, so React never needs to know it happened. */
  const hoveredTokenRef = useRef<string | null>(null);

  // ── Measure (`useMeasureTool`) ──────────────────────────────────────────────────────────────
  // An end binds to the token under the pointer (the same `hitTestToken` the hover reveal uses),
  // anchored on the cell under the pointer, else the bare cell. The feed and the debug ref are read through refs by the window release net and the
  // mount-time debug hook, which are both subscribed once.
  const measureEndAt = (world: { x: number; y: number }): MeasureEnd => {
    const hit = hitTestToken(combatants, world);
    return measureEndFor(hit?.id ?? null, cellAt(world));
  };
  // The Measure press's screen point, so its release can tell a drag from a click (`isMeasureDrag`).
  const measurePressAtRef = useRef<{ x: number; y: number } | null>(null);
  const measurePointerRef = useRef(onMeasurePointer);
  measurePointerRef.current = onMeasurePointer;
  const measureDebugMirror = useRef<MeasureDebug>({ local: null, remote: [] });
  measureDebugMirror.current = measureDebugRef?.current ?? { local: null, remote: [] };

  // ── Live move-path preview (this feature) ────────────────────────────────────────────────────
  // While we drag a token, stream the route its drop would take (the SAME `movePathCells` set drawn
  // locally) to everyone else, so they see a ghost trail of the impending move. Read `onMovePreview`
  // through a ref so the sender stays stable. No throttle: the drag handler already coalesces to
  // cell-change granularity (one emit per cell crossed), so this fires far slower than the cursor
  // stream. `null` clears the trail (drop / illegal hover).
  const streamMovePreview = useMovePreviewStream(onMovePreview);

  const spaceHeld = useSpaceHeld();

  // Left-drag pans only under the sticky hand tool or a held space; otherwise the left button belongs
  // to the armed tool (select/move, or the brush). Middle-drag pans on top of this, always (below).
  const canPanLeft = tool === "pan" || spaceHeld;

  const {
    strokeRef,
    tilePreview,
    setTilePreview,
    hoverCell,
    setHoverCell,
    finishStroke,
    tileBoxRef,
    tileBoxPreview,
    setTileBox,
    finishBox,
    wallRef,
    wallPreview,
    setWall,
    finishWall,
    roomRef,
    roomPreview,
    setRoom,
    finishRoom,
  } = useAuthoringGestures({ brush, tile, coverGrade, hazard, eraseMode, terrain, edges, rooms, autoWall, wallHeight, wallCoverGrade, cols, rows, actorId, dispatch });

  // ── Token drag ──────────────────────────────────
  //
  // "The renderer moves it live, state commits on drop" — the SAME shape carried across the migration,
  // ported onto the native-DOM `mousedown`→`mousemove`→`mouseup` gesture already established for
  // every other drag here (tile box / wall run / room box), per the ratified interaction model.
  // `beginTokenDrag`'s hit-test itself (in `onMouseDown`) uses `hitTestToken` (`combatCells.ts`) —
  // pure footprint-AABB math, not a Pixi `hitArea`. `boardHandleRef.current.moveToken(...)` mutates
  // the live Pixi container every `mousemove`; the drop dispatches `move_token`/`place_token` exactly
  // like the placement-click path does, and a REJECTED drop snaps the container back to rest.
  type TokenDragState = {
    id: string;
    /** The world point (px, scale-1) the press landed at — deltas off this drive the live move. */
    pressWorld: { x: number; y: number };
    /** The token's footprint top-left at press time (world px) — the drag's origin + snap-back target. */
    startPx: number;
    startPy: number;
    /** The anchor's offset within its own footprint box. */
    offX: number;
    offY: number;
    /** The last world top-left `moveToken` was told (kept so an off-canvas release, caught only by
     *  the window `mouseup` net below, still has a coordinate to finish the drop with). */
    lastTopLeft: { x: number; y: number };
    /** The last drop-anchor cell key sampled, so the path-preview recompute is cell-change-coalesced
     *  rather than firing on every pixel of pointer movement. */
    lastDestKey: string | null;
    /** The dragged combatant snapshot at press time — read only to
     *  build the PLAY drag-ghost's look (`createGhost`); EDIT's live-node drag never touches it. */
    combatant: Combatant;
    /** True once the PLAY drag-ghost has been created for this drag (lazily, on the first real
     *  pointer travel — a stationary press/click never spins one up). */
    ghostStarted: boolean;
    /** `commit`, or `planning` (off-turn, `projects/off-turn-planning.md` 2.3): ghost, path dots and
     *  forecast only, and a release that dispatches nothing. Fixed at press. */
    kind: DragKind;
    /** The footprint's side in cells, so the callout can centre over the ghost. */
    footprintN: number;
    /** This drag's identity token (see `latestDragTokenRef`) — lets a settle callback armed by an
     *  EARLIER drag on this same token id recognize it's stale and must not tear down a NEWER drag's
     *  ghost (a fast re-drag of the same token before the first drop's walk settles). */
    dragToken: number;
    /** A Trampler's trail so far (`trampleTrail`, Q3): the anchor path the drop sends. `null` for
     *  any other mover, and until the first cell change. */
    trail: Cell[] | null;
    /** The trail's drop would be refused (over the move left, or the pointer's cell unreachable). */
    trailRefused: boolean;
  };
  const tokenDragRef = useRef<TokenDragState | null>(null);
  // A slow unit's drag draws plain rings per cell and asks for cover-aware ones once the pointer has
  // rested on a cell (`range-preview-cover` F4).
  const dragRingsRestRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearDragRings = useCallback(() => {
    if (dragRingsRestRef.current !== null) clearTimeout(dragRingsRestRef.current);
    dragRingsRestRef.current = null;
    setDragRings(null);
  }, []);
  /** The drag's rings at `dest` (none over an illegal drop), and the rest timer for a slow unit's plain ones. */
  const showDragRings = useCallback(
    (args: { id: string; dest: SquareCell; legal: boolean }) => {
      const { id, dest, legal } = args;
      if (dragRingsRestRef.current !== null) clearTimeout(dragRingsRestRef.current);
      dragRingsRestRef.current = null;
      const at = legal ? (threatRingsAt?.(id, dest) ?? null) : null;
      setDragRings(at ?? NO_RINGS);
      if (at === null || at.settled) return;
      const destKey = squareKey(dest);
      dragRingsRestRef.current = setTimeout(() => {
        dragRingsRestRef.current = null;
        if (tokenDragRef.current?.lastDestKey !== destKey) return;
        setDragRings(threatRingsAt?.(id, dest, true) ?? NO_RINGS);
      }, DRAG_RINGS_REST_MS);
    },
    [threatRingsAt],
  );
  /** The ghost's callout (2.4/3.2): content set per drag cell (`calloutContent`), position written
   *  straight to the element every `mousemove` (`placeGhostCallout`) so the stage doesn't re-render
   *  per pixel. `null` hides it. */
  const [callout, setCallout] = useState<{ heading: string | null; rows: ForecastRow[] } | null>(null);
  const calloutElRef = useRef<HTMLDivElement | null>(null);
  const calloutAnchorRef = useRef<CalloutAnchor | null>(null);
  /** Monotonic counter minting each drag's `dragToken` — see
   *  `TokenDragState.dragToken`'s doc. */
  const dragTokenCounterRef = useRef(0);
  /** The most recently ARMED drag's token per combatant id — a settle callback compares its captured
   *  `dragToken` against this map's current value before clearing a ghost, so a superseded drag's
   *  eventual settle never wipes a newer one's still-active ghost. */
  const latestDragTokenRef = useRef<Map<string, number>>(new Map());
  /** Combatant ids this client currently believes have an outstanding PLAY move (`noteMoveHeld(id,
   *  true)` called, not yet released) —'s strand guard. Populated in
   *  `finishTokenDrag`'s accepted-PLAY-drop branch, drained either by that drag's own settle callback
   *  (the normal case) or by the {@link moveRejectionSeq} effect below (a server-side reject that
   *  produces no diff, so no beat will ever come to drain it the normal way). */
  const outstandingMoveIdsRef = useRef<Set<string>>(new Set());
  /** Release every drag this client still believes is outstanding — the shared body of both strand
   *  guards ({@link moveRejectionSeq} for a server-side reject, {@link undoRedoSeq} for a rewind). The
   *  signals carry no combatant id, so this can't narrow to just one drag; see the props' docs for why
   *  over-releasing is the safer default. `noteMoveHeld(id, false)` is a no-op if that id's beat already
   *  queued and is mid-drain (nothing strands OR clears twice), so it's safe to fire mid-walk too. */
  const releaseOutstandingGhosts = useCallback(() => {
    for (const id of outstandingMoveIdsRef.current) noteMoveHeld(id, false);
    outstandingMoveIdsRef.current.clear();
  }, [noteMoveHeld]);
  /** Skips the {@link moveRejectionSeq} effect's release on mount (no rejection has actually happened
   *  yet — the prop just arrived at its initial value). */
  const prevMoveRejectionSeqRef = useRef(moveRejectionSeq);
  useEffect(() => {
    if (moveRejectionSeq === prevMoveRejectionSeqRef.current) return;
    prevMoveRejectionSeqRef.current = moveRejectionSeq;
    releaseOutstandingGhosts(); // a fresh rejection landed
  }, [moveRejectionSeq, releaseOutstandingGhosts]);
  /** Same strand-guard release on an undo/redo (`ghost-drag-movement` follow-up): a rewind voids any
   *  in-flight PLAY-move ghost, and an undo-to-origin produces no positional diff so `ingest` alone
   *  would keep it held forever (see {@link undoRedoSeq}'s doc). Skips the mount tick like the above. */
  const prevUndoRedoSeqRef = useRef(undoRedoSeq);
  useEffect(() => {
    if (undoRedoSeq === prevUndoRedoSeqRef.current) return;
    prevUndoRedoSeqRef.current = undoRedoSeq;
    releaseOutstandingGhosts();
  }, [undoRedoSeq, releaseOutstandingGhosts]);

  /** Affordance mirror of "is this token draggable right now", and as what — EDIT-mode `place_token`
   *  is free authoring placement (no turn gate) as long as the actor controls it; PLAY-mode
   *  `move_token` additionally needs `canPlayMove` to COMMIT, and otherwise opens a PLANNING drag
   *  when the viewer may plan the unit's move (`isPlanningMove`, off-turn). `null`: no drag. */
  const dragKind = (c: Combatant): DragKind | null =>
    dragKindFor({
      controls: canControl(c),
      placing: moveCommandType === "place_token",
      canPlayMove: canPlayMove(c),
      planning: moveCommandType === "move_token" && isPlanningMove(c.id),
    });

  // Send an accepted PLAY drop as a `request_move`, with the Boost card's answers when it asked.
  // PLAY never `onLocalMove`s/`commitLocalMove`s here (D1): the dragger walks off the authoritative
  // echo like everyone else.
  const sendPlayDrop = useCallback((drop: PlayDrop, choices?: Record<string, string>) => {
    const result = dispatch(
      { type: "move_token", actorId, combatantId: drop.id, to: drop.dest, ...(drop.path !== null ? { path: drop.path } : {}) },
      choices !== undefined ? { choices } : {},
    );
    if (result.ok) {
      boardHandleRef.current?.snapGhost(drop.id, drop.ghostAt);
      // Mark the move outstanding for the WHOLE window from here through its own terminal resolution
      // — NOT only while a checkpoint genuinely holds it. This closes
      // the premature-settle race: `PresentationDirector.ingest`'s `sweepImmediateSettles` runs on
      // EVERY reconcile, and without this an unrelated authoritative broadcast landing between this
      // accepted drop and our own move's echo would see no beat queued for `drop.id` yet and settle
      // (clear) the ghost a beat early. Held true uniformly covers the ordinary in-flight gap AND a
      // real Overwatch hold — `watchSettle`'s callback below is what actually fires the teardown
      // either way once a beat is queued and drains (or immediately if nothing ever queues).
      noteMoveHeld(drop.id, true);
      outstandingMoveIdsRef.current.add(drop.id);
      const myDragToken = drop.dragToken;
      watchSettle(drop.id, () => {
        // Release the hold — a no-op if the pump's own beat-drain cleanup already cleared it (the
        // common case), but load-bearing for D2(iii)'s reject/no-walk shape, where nothing else ever
        // clears it. Unconditional (not dragToken-guarded): a settle fired for `drop.id` at all means
        // ITS move concluded, and this drop is the one that armed the hold in the first place.
        noteMoveHeld(drop.id, false);
        outstandingMoveIdsRef.current.delete(drop.id);
        // A NEWER drag on this same id may have superseded this one (a fast re-drag before this
        // drop's walk settled) — only clear the ghost if this is still the latest drag for `drop.id`.
        if (latestDragTokenRef.current.get(drop.id) === myDragToken) boardHandleRef.current?.clearGhost(drop.id);
      });
    } else {
      // Rejected client-side (out of turn / over budget / etc.) — no walk will ever happen, nothing
      // to await: tear the ghost down immediately. `noteMoveHeld` was never set for this drag (the
      // reject is synchronous, before any hold could be armed), so there's nothing to release.
      boardHandleRef.current?.clearGhost(drop.id);
    }
  }, [dispatch, actorId, watchSettle, noteMoveHeld]);

  // Commit the in-progress token drag: resolve the last-known top-left back to an anchor cell and
  // dispatch the move. EDIT (`place_token`) is UNCHANGED from before `ghost-drag-movement`: an
  // ACCEPTED drop commits to presentation immediately (`onLocalMove` #5 — the drag
  // already showed the travel, so the server echo diffs to a no-op); a REJECTED drop snaps the live
  // Pixi container back to its rest position; a SAME-CELL drop also snaps back, because no move fires
  // so nothing else would ever pull the sprite off its drag offset (see the guard below). PLAY
  // (`move_token`) never moved the real node during the drag at all (D1) — only the ghost travelled —
  // so its three outcomes resolve the GHOST instead: snap-to-drop-cell + await `watchSettle` on an
  // accepted drop (the real token then walks off the authoritative echo like every other client, the
  // diff-path `ingest` — `token-slide-parity.e2e.ts`'s proven walk), or an immediate `clearGhost` on a
  // reject / same-cell resolve (nothing will ever animate, so there's no beat to await). A PLAY drop
  // whose folded Boost has opt-in riders holds the ghost and opens the Boost card instead.
  const finishTokenDrag = useCallback(() => {
    const drag = tokenDragRef.current;
    tokenDragRef.current = null;
    if (!drag) return;
    setMovePath(null);
    setMovePathRefused(false);
    setTrampleRingIds(null);
    setDragPastReach(false);
    clearDragRings();
    setCallout(null);
    calloutAnchorRef.current = null;
    // A planning drag never streamed a trail, so it has none to clear (and sends nothing at all).
    if (streamsMovePreview(drag.kind)) streamMovePreview(null);
    const isPlay = moveCommandType === "move_token";
    // A press that never actually moved is a plain click (select-only, or the first half of a
    // dblclick-to-inspect) — this guard is the drag-distance threshold a stationary press needs.
    // Without it, EVERY click on a draggable/controllable token would issue a spurious
    // `move_token`/`place_token` to its own current cell (harmless-looking but a real extra command,
    // and it raced the click-to-fire path for an armed attack in testing). PLAY never spins up a ghost
    // until the first real pointer travel (see `onMouseMove`), so there is nothing to tear down here.
    const destMinCorner = { col: Math.round(drag.lastTopLeft.x / CELL), row: Math.round(drag.lastTopLeft.y / CELL) };
    const outcome = dropOutcome({
      kind: drag.kind,
      moved: drag.lastTopLeft.x !== drag.startPx || drag.lastTopLeft.y !== drag.startPy,
      sameCell: destMinCorner.col === drag.startPx / CELL && destMinCorner.row === drag.startPy / CELL,
    });
    if (outcome === "click") return;
    // A PLANNING drag (off-turn, 2.3) ends here, before anything below can dispatch: the plan lasts
    // while the mouse is down, and its release clears the ghost and commits nothing — no
    // `move_token`/`request_move`, no `noteMoveHeld`, no `watchSettle`.
    if (outcome === "planning") {
      boardHandleRef.current?.clearGhost(drag.id);
      return;
    }
    // A drag that travelled but resolves back to the ORIGIN cell isn't a move: the sprite was pulled
    // to an offset within its own cell, so the resting anchor never changes, no `move_token` fires,
    // and — crucially — no `setTokens` reconcile would ever reposition the container (the reconciler
    // only writes back on a resting-anchor change). Left alone the sprite stays stuck at that offset.
    // Snap it back to rest directly and skip the no-op dispatch (a same-cell move is a spurious
    // command, exactly what the stationary-press guard above avoids). `startPx`/`startPy` are the
    // origin footprint min-corner (`minX/minY * CELL`), so they double as its exact rest anchor.
    if (outcome === "snap-back") {
      if (isPlay) boardHandleRef.current?.clearGhost(drag.id); // the real node never left home either
      else boardHandleRef.current?.moveToken(drag.id, { x: drag.startPx, y: drag.startPy });
      return;
    }
    const dest = anchorFromMinCorner(destMinCorner, { x: drag.offX, y: drag.offY });
    // A Trampler's refused trail sends nothing: the red trail already said why.
    if (isPlay && drag.trail !== null && drag.trailRefused) {
      boardHandleRef.current?.clearGhost(drag.id);
      return;
    }
    if (!isPlay) {
      const result = dispatch({ type: moveCommandType, actorId, combatantId: drag.id, to: dest });
      if (result.ok) onLocalMove(drag.id, dest);
      else boardHandleRef.current?.moveToken(drag.id, { x: drag.startPx, y: drag.startPy });
      return;
    }
    const drop: PlayDrop = { id: drag.id, dest, path: drag.trail, ghostAt: { x: destMinCorner.col * CELL, y: destMinCorner.row * CELL }, dragToken: drag.dragToken };
    // A drop that folds a Boost with opt-in riders asks first: the ghost holds at the drop cell and
    // the Boost card sends (or cancels). Nothing is dispatched, so no halt prediction or
    // auto-activation candidate is armed until the card confirms.
    const riders = boostDropRiders?.(drag.id, dest, drag.trail ?? undefined) ?? null;
    if (riders !== null) {
      boardHandleRef.current?.snapGhost(drag.id, drop.ghostAt);
      setBoostDrop({ ...drop, riders });
      return;
    }
    sendPlayDrop(drop);
  }, [dispatch, actorId, moveCommandType, onLocalMove, streamMovePreview, boostDropRiders, sendPlayDrop]);

  // Esc mid-drag (layer 2): the token snaps back to its origin and no move is sent — the snap-back
  // outcome above, reached without a release. The button may still be held; its mouseup then finds
  // no drag and commits nothing.
  const cancelTokenDrag = useCallback(() => {
    const drag = tokenDragRef.current;
    tokenDragRef.current = null;
    if (!drag) return;
    setMovePath(null);
    setMovePathRefused(false);
    setTrampleRingIds(null);
    setDragPastReach(false);
    clearDragRings();
    setCallout(null);
    calloutAnchorRef.current = null;
    if (streamsMovePreview(drag.kind)) streamMovePreview(null);
    if (moveCommandType === "move_token") boardHandleRef.current?.clearGhost(drag.id);
    else boardHandleRef.current?.moveToken(drag.id, { x: drag.startPx, y: drag.startPy });
  }, [moveCommandType, streamMovePreview]);

  // The Boost card's two exits: BOOST & MOVE sends the held drop with the card's answers; CANCEL (or
  // Esc, or a new drag) snaps the ghost back and sends nothing.
  const confirmBoostDrop = useCallback(
    (choices: Record<string, string>) => {
      if (!boostDrop) return;
      setBoostDrop(null);
      sendPlayDrop(boostDrop, choices);
    },
    [boostDrop, sendPlayDrop],
  );
  const cancelBoostDrop = useCallback(() => {
    if (!boostDrop) return;
    setBoostDrop(null);
    boardHandleRef.current?.clearGhost(boostDrop.id);
  }, [boostDrop]);

  const {
    drawCtx,
    freehandRef,
    freehandPreview,
    setFreehandPreview,
    finishFreehand,
    polylineRef,
    polylinePreview,
    setPolylinePreview,
    polylineCursor,
    setPolylineCursor,
    polylineRawClicksRef,
    finishPolyline,
    dropPolyline,
    eraserRef,
    openEraser,
    sampleEraser,
    finishEraser,
    dropEraser,
    erasingIds,
  } = useDrawGestures({ drawColor, actorId, dispatch, isGm, annotations });

  // The eraser's pale ring under the pointer, at its real reach (`ERASER_HIT_RADIUS`), shown while
  // Erase is armed and the pointer is on the board.
  const eraserRingRef = useRef<HTMLDivElement>(null);
  // Strokes the open eraser drag has collected leave this viewer's board at once; they're removed for
  // everyone when the drag commits on release.
  const boardAnnotations = useMemo(() => visibleAnnotations(annotations, erasingIds), [annotations, erasingIds]);

  const { selectedAnnotationId, annotationInspectorOpen, selectAnnotation, inspectAnnotation, deselectAnnotation } =
    useAnnotationSelection(annotations);
  // While Draw is armed, the annotation shapes must NOT claim the click/dblclick — a vertex click
  // or a freehand drag-start landing inside an existing stroke's widened hit region has to reach
  // the stage's own mousedown-driven gesture wiring untouched (mousedown itself always bubbles
  // through regardless — only `click`/`dblclick` need this guard). This is also the Gotcha's fix:
  // the native `dblclick` event is a same-element-within-the-browser's-timing-window fire (see the
  // doc on `polylineRawClicksRef` above), so a burst of rapid polyline-placing clicks that happen to
  // land twice on the SAME existing stroke would otherwise fire a spurious `dblclick` on it,
  // popping the recolor/delete inspector mid-draw. Gating both handlers on `drawTool === null`
  // makes them a hard no-op (no `cancelBubble`, no state change) whenever Draw is armed, so that
  // burst can never reach `onInspect`.
  const annotationSelectable = drawTool === null;

  // The host (`CombatApp`'s `pickDrawTool`/`pickBrush`/`pickTool`) can change `drawTool` directly —
  // arming the OTHER draw brush, or disarming Draw entirely for the terrain rail. (Esc never reaches
  // this with a gesture open: layer 2 aborts the gesture before layer 3 disarms Draw.) Settle whatever gesture was open under the
  // PREVIOUS `drawTool` whenever it changes: an accumulating polyline's clicks were deliberate, so
  // it FINISHES (same commit `finishPolyline` always does); an open freehand drag OR an open eraser
  // drag has nothing committed yet, so both are simply ABANDONED (close path 4) — same treatment,
  // never a partial erase. A `useEffect` cleanup fires exactly on that transition (and once more on
  // unmount, which doubles as close path 5 — nulling a ref is safe to run at unmount too) — cheap
  // and idempotent when nothing was open (every finisher/reset already no-ops on a null ref).
  useEffect(() => {
    return () => {
      finishPolyline();
      freehandRef.current = null;
      setFreehandPreview(null);
      dropEraser();
    };
  }, [drawTool, finishPolyline, dropEraser]);

  // A release that lands off the canvas never reaches the stage's `onMouseUp`; this window net
  // commits the open gesture (tile stroke, wall run, room box, token drag, or freehand annotation)
  // anyway so no work is lost (and no stale gesture leaks into the next press). All finalizers are
  // idempotent and independent — `finishTokenDrag` uses the drag's last-sampled position (the same
  // "off-canvas release keeps the last known point" behavior every other gesture here already has).
  // The polyline is NOT here — it commits on double-click/Enter, never on a plain mouseup (each
  // mousedown only adds a vertex).
  useEffect(() => {
    // CONTRACT for every window-level release net on this surface: take the event and filter to the
    // PRIMARY button. A non-primary release must never end a gesture. This one was written without
    // an event parameter at all (`const onUp = () => …`), which made it fire for any button — the
    // defect below. The shape is easy to reproduce, so state the rule here rather than only fixing
    // the instance: if you add another window-level `mouseup`/`pointerup` listener, it needs the
    // same guard, and a button-filter on the element handler alone will NOT cover you (a release
    // over the canvas runs both paths).
    const onUp = (e: MouseEvent) => {
      // PRIMARY releases only — the same rule `onMouseUp` applies on the canvas, and for the same
      // reason: every gesture below was opened by a LEFT press, so a middle/right release must not
      // finish it. This net is the path that actually bites: without the guard, a right-press-to-pan
      // during a live token drag ends the drag at the release point (the token commits mid-board,
      // several cells from where the user is still dragging it). Guarding only the canvas handler is
      // not enough; this is the path a release over the canvas takes too.
      if (e.button !== 0) return;
      finishTokenDrag();
      finishStroke();
      finishBox();
      finishWall();
      finishRoom();
      finishFreehand();
      finishEraser(); // close path 2 — an off-canvas eraser release still commits what was collected
      // An off-canvas release ends a Measure drag (a release over the canvas already ran `onMouseUp`).
      measurePressAtRef.current = null;
      measurePointerRef.current?.({ phase: "release", dragged: true });
      cancelPing(); // an off-canvas release also abandons an in-flight ping hold
    };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, [finishTokenDrag, finishStroke, finishBox, finishWall, finishRoom, finishFreehand, finishEraser, cancelPing]);

  // Esc ABANDONS an in-progress authoring drag: it
  // nulls every gesture ref + preview WITHOUT committing (unlike a release, which commits). The
  // user may still be holding the button; the subsequent mouseup then finds null refs and commits
  // nothing. Covers the tile box, the freehand stroke (tile brush), the wall run, the room box,
  // and — Draw's own freehand drag AND the eraser drag — the SAME abandon-a-drag treatment: an in-progress erase's already-collected ids are dropped
  // entirely, nothing is removed.
  const cancelGesture = useCallback(() => {
    strokeRef.current = null;
    setTilePreview(null);
    setTileBox(null);
    setWall(null);
    setRoom(null);
    freehandRef.current = null;
    setFreehandPreview(null);
    dropEraser();
  }, [setTileBox, setWall, setRoom, dropEraser]);
  // The stage's Esc entries (`decisions/0189`): each in-progress gesture is a standing layer-2 entry,
  // live while its ref holds the gesture, and Esc aborts it without committing. A half-drawn
  // polyline is DROPPED (Enter and double-click still finish it; Draw stays armed). The selected
  // annotation is a layer-6 entry. The stage adds no Esc listener of its own.
  const authoringDragOpen = (): boolean =>
    strokeRef.current !== null ||
    tileBoxRef.current !== null ||
    wallRef.current !== null ||
    roomRef.current !== null ||
    freehandRef.current !== null ||
    eraserRef.current !== null;
  useEscEntry(esc, { layer: "gesture", id: "stage-drag", active: true, live: authoringDragOpen, onDismiss: cancelGesture });
  useEscEntry(esc, { layer: "gesture", id: "stage-polyline", active: true, live: () => polylineRef.current !== null, onDismiss: dropPolyline });
  useEscEntry(esc, { layer: "gesture", id: "stage-token-drag", active: true, live: () => tokenDragRef.current !== null, onDismiss: cancelTokenDrag });
  useEscEntry(esc, { layer: "gesture", id: "stage-boost-drop", active: boostDrop !== null, onDismiss: cancelBoostDrop });
  useEscEntry(esc, { layer: "gesture", id: "stage-ping-hold", active: true, live: () => pingPressRef.current !== null, onDismiss: cancelPing });
  useEscEntry(esc, { layer: "annotation", id: "annotation-selection", active: selectedAnnotationId !== null, onDismiss: deselectAnnotation });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" && polylineRef.current) {
        e.preventDefault();
        finishPolyline();
        return;
      }
      // Delete/Backspace removes the SELECTED annotation — fired for whatever is
      // selected regardless of who authored it; ownership is never checked here (the tiny
      // inspector's recolor/delete controls are the only place that check IS made, and only as an
      // affordance). The real wall is the server gate (`canIssueEncounter`):
      // a cross-owner attempt round-trips a rejection (surfaced by the app's existing generic
      // rejection toast) and the stroke simply stays put. Guarded off any text field so a
      // Backspace while renaming a room / typing in chat is never swallowed.
      if ((e.key === "Delete" || e.key === "Backspace") && selectedAnnotationId && !isTextTarget(e.target)) {
        e.preventDefault();
        const { actorId, dispatch } = drawCtx.current;
        dispatch(removeAnnotationCommand({ actorId, annotationId: selectedAnnotationId }));
        deselectAnnotation();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [finishPolyline, deselectAnnotation, selectedAnnotationId]);

  const boardW = Math.max(1, cols) * CELL;
  const boardH = Math.max(1, rows) * CELL;

  // The single chokepoint: every `view` write funnels through this wrapper, which resolves an
  // updater (or accepts a plain object, same as the raw setState) against the CURRENT view and
  // clamps the result to the board rect (`clampView`) before it ever reaches `setViewRaw`. The four
  // writers (initial-center, wheel-zoom, pan-drag, camera-focus) keep their exact call shape — they
  // just cannot escape the bound, and a fifth writer added later inherits it for free.
  //
  // `setView`'s identity MUST be stable (`useCallback` with `[]` deps): `onPanMove`/`onPanEnd`
  // below are added/removed from `window` by reference (`beginPan`/`onPanEnd`), so if `setView` took
  // a dependency on `size`/`boardW`/`boardH` and were listed in `onPanMove`'s deps, a viewport resize
  // mid-drag would give `onPanMove` a new identity — `onPanEnd` would then try to remove a listener
  // that was never added, leaking window listeners on a live gesture. So the clamp's non-view inputs
  // live in a ref, refreshed every render below, and `setView` reads the ref from inside the
  // `setViewRaw` updater rather than closing over the render-scoped values directly.
  const clampInputsRef = useRef({ size, boardW, boardH });
  clampInputsRef.current = { size, boardW, boardH };
  const setView = useCallback((next: View | ((v: View) => View)) => {
    setViewRaw((v) => {
      const requested = typeof next === "function" ? next(v) : next;
      const { size: viewport, boardW: bw, boardH: bh } = clampInputsRef.current;
      return clampView(requested, viewport, { width: bw, height: bh });
    });
  }, []);

  // ── The Pixi mount ──────────────────────────────────────────────────────────────────────────
  // `hostRef` is the empty div `mountCombatBoard` appends its WebGL canvas into (the thin
  // imperative-mount shape). `boardHandleRef` is
  // the returned handle; `boardReady` flips once the async `mountCombatBoard` resolves, so the sync
  // effects below (view/board/terrain/rooms/authoringPreview) know to push their current value the
  // moment the handle exists, not just on their own dep changes (which may already have fired once
  // against a still-null handle).
  const hostRef = useRef<HTMLDivElement>(null);
  const boardHandleRef = useRef<CombatBoardHandle | null>(null);
  const [boardReady, setBoardReady] = useState(false);

  // Tell the shell's scene cut the board has drawn (`SceneCut.tsx`): the sync effects push the state
  // in the commit `boardReady` lands, and the ticker paints it within the next two frames.
  const sceneReady = useStage()?.sceneReady;
  useEffect(() => {
    if (!boardReady || !sceneReady) return;
    let raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(sceneReady);
    });
    return () => cancelAnimationFrame(raf);
  }, [boardReady, sceneReady]);

  // The command menu's target rings (`targetRings`), pushed whole on every change; a newly-ringed
  // target spins in once. A still ring, and no spin, under `prefers-reduced-motion`.
  // A Trampler's drag outlines who its trail tramples instead, for the drag's length (no spin).
  const ringKey = trampleRingIds
    ? `trample|${trampleRingIds.join(",")}`
    : targetRings
      ? `${targetRings.kind}|${targetRings.phase}|${targetRings.ids.join(",")}`
      : "";
  useEffect(() => {
    const handle = boardReady ? boardHandleRef.current : null;
    if (!handle) return;
    if (trampleRingIds) {
      handle.setTargetRings(trampleRingIds, { kind: "attack", phase: "armed", pulse: false, spin: false });
      return;
    }
    if (!targetRings) {
      handle.setTargetRings([], { kind: "attack", phase: "hover", pulse: false, spin: false });
      return;
    }
    const still = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    handle.setTargetRings(targetRings.ids, { kind: targetRings.kind, phase: targetRings.phase, pulse: !still, spin: !still });
    // `ringKey` carries every field of `targetRings`, so a new object with the same content is a no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardReady, ringKey]);

  // The armed attack's mini forecast badges. The map is memoized upstream per aim and state, and the
  // board itself skips a re-place when only `expandedTargetId` moved, so a hover costs a visibility flip.
  useEffect(() => {
    const handle = boardReady ? boardHandleRef.current : null;
    if (!handle) return;
    handle.setTargetBadges(targetBadges ?? new Map(), { expandedId: expandedTargetId ?? null });
  }, [boardReady, targetBadges, expandedTargetId]);

  // The Stage needs explicit pixel dimensions; track the wrapper's content box.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setSize({ width: Math.floor(r.width), height: Math.floor(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Push every wrapper resize into the renderer. Pixi's `resizeTo: host` does NOT cover this: its
  // ResizePlugin only listens to `window`'s `"resize"` event, so a host that changes size because
  // the LAYOUT changed — the COMMS sidebar docking, any flex sibling changing width — leaves the canvas frozen at its old pixel size, showing a dead band of stage
  // background where the board should be (the EDIT-mode bottom gap). `useLayoutEffect` so the
  // canvas is resized in the same frame the DOM reflowed, never one frame of stale canvas behind.
  useLayoutEffect(() => {
    if (!boardReady || size.width === 0 || size.height === 0) return;
    boardHandleRef.current?.resize();
    // Re-clamp the pan against the NEW viewport — a shrink (entering PLAY) can otherwise leave the
    // board parked outside the bounds `clampView` would allow, with no way to scroll back.
    setView((v) => v);
  }, [boardReady, size, setView]);

  // Once we know the viewport, drop the board in its center at 1:1.
  useEffect(() => {
    if (centered.current || size.width === 0 || size.height === 0) return;
    setView({
      scale: 1,
      x: Math.round((size.width - boardW) / 2),
      y: Math.round((size.height - boardH) / 2),
    });
    centered.current = true;
  }, [size, boardW, boardH]);

  // The pointer's position relative to the Pixi host div (screen px), computed straight from the
  // native event — there's no stage object to ask for a pointer position, only the host div's own
  // bounding rect.
  const screenPoint = (e: MouseEvent | WheelEvent): { x: number; y: number } | null => {
    const el = hostRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  // The stage's current pointer in world coords (px at scale 1), or null when the pointer isn't
  // over the stage. The inverse of the view transform — the one place click/zoom/paint share.
  const worldPoint = (screen: { x: number; y: number } | null): { x: number; y: number } | null => {
    if (!screen) return null;
    return { x: (screen.x - view.x) / view.scale, y: (screen.y - view.y) / view.scale };
  };

  // Wheel zoom, anchored to the cursor: keep the world point under the pointer fixed.
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const screen = screenPoint(e);
    const world = worldPoint(screen);
    if (!screen || !world) return;
    const raw = e.deltaY > 0 ? view.scale / ZOOM_STEP : view.scale * ZOOM_STEP;
    const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, raw));
    setView({ scale, x: screen.x - world.x * scale, y: screen.y - world.y * scale });
  };
  const { suppressClickRef, panning, beginPan } = useStagePan({ view, setView, cancelPing, streamCursor, screenPoint, worldPoint });

  // Is one of the left-drag gestures currently holding the surface? Every ref below is opened on a
  // left press and closed on the matching release, so a pan started on top of one would strand it
  // (see {@link opensPan}). `polylineRef` is deliberately absent — it accumulates by click, not by
  // drag, so there's no held press to strand and panning between its vertices stays legal.
  const gestureOpen = (): boolean =>
    tokenDragRef.current !== null ||
    freehandRef.current !== null ||
    eraserRef.current !== null ||
    strokeRef.current !== null ||
    tileBoxRef.current !== null ||
    wallRef.current !== null ||
    roomRef.current !== null ||
    pingPressRef.current !== null;

  // Pointer-down: two paths. A pan-opening press (`opensPan` — middle and right always, in every
  // tool, even over a token; left only when `canPanLeft`, i.e. the sticky Pan tool or held space;
  // and none of them while another gesture is already in flight) starts a manual pan. A native-DOM
  // middle/right press only ever reaches `beginPan`, so it can never grab a token instead.
  // Otherwise the left button opens whichever gesture is armed — a tile brush's freehand stroke at
  // the press point (the press cell fixes the whole stroke's paint-vs-clear direction,
  // `tileStrokeCommand`), the wall tool's rubber-band run, the room tool's box, Draw, or a ping hold.
  const onMouseDown = (e: MouseEvent) => {
    if (opensPan(e.button, canPanLeft, gestureOpen())) {
      // A fresh press supersedes any stale "just pinged" flag (it is consumed by the trailing click
      // on an empty-board ping, but a token ping's click is swallowed by the token, so clear it here
      // so it can never leak into a later, unrelated click).
      justPingedRef.current = false;
      tokenPressRef.current = false;
      // Middle's autoscroll and right's native menu would both fight the drag; the left hand has no
      // default to cancel (and must not eat its own press).
      if (panNeedsPreventDefault(e.button)) e.preventDefault();
      beginPan(e);
      return;
    }
    // A non-primary press that did NOT open a pan is a press this surface has no use for — either
    // a thumb button, or (the case the guard exists for) middle/right arriving mid-gesture. Bail
    // before touching any state: it isn't a fresh gesture press, so clearing the press flags above
    // would corrupt the gesture that IS in flight (`tokenPressRef` is what stops the drag's
    // trailing click from also deselecting).
    if (e.button !== 0) return;
    justPingedRef.current = false;
    tokenPressRef.current = false;
    cyclePressRef.current = null;
    const world = worldPoint(screenPoint(e));
    // Measure owns the left button while armed: a press starts or dismisses a ruler, and a press on
    // a token binds that unit rather than selecting, dragging or arming a ping hold.
    if (measureArmed) {
      if (world) {
        measurePressAtRef.current = { x: e.clientX, y: e.clientY };
        onMeasurePointer?.({ phase: "press", end: measureEndAt(world) });
      }
      return;
    }
    // Inspect is read-only: a press (a touch tap arrives as one) inspects the cell under it and does
    // nothing else, so it never selects, drags or pings.
    if (inspectArmed) {
      if (world) {
        const c = cellAt(world);
        reportHoverCell(c.x >= 0 && c.x < cols && c.y >= 0 && c.y < rows ? c : null);
      }
      return;
    }
    // Erase: the momentary Alt-held modifier OR the sticky `eraseMode` toggle (X) — captured once at press.
    const erase = e.altKey || eraseMode;
    // Draw CLAIMS the left button first, ahead of the terrain rail/ping ('s
    // precedence rule) — in practice the host already keeps `drawTool` and `brush`/`tool`
    // mutually exclusive, but checking it first here means that guarantee doesn't have to be
    // perfect for Draw to behave correctly. Freehand opens a drag; polyline accumulates a vertex
    // per click (deduped — see `accumulatePolylineVertex`) and is finished via double-click/Enter (Esc drops it).
    if (drawTool === "freehand" && world) {
      freehandRef.current = [world];
      setFreehandPreview([world]);
    } else if (drawTool === "eraser" && world) {
      // Opens the erase drag; the press point itself is tested too, so a click erases what's under it.
      openEraser(world);
    } else if (drawTool === "polyline" && world) {
      // Track this raw click alongside the immediately preceding one — `onStageDblClick` uses the
      // PAIR to tell a genuine same-spot finishing double-click from the native `dblclick` event
      // firing after any two rapid clicks (see `polylineRawClicksRef`'s doc comment).
      const prevRaw = polylineRawClicksRef.current;
      polylineRawClicksRef.current = [prevRaw ? prevRaw[1] : world, world];
      const next = accumulatePolylineVertex(polylineRef.current ?? [], world);
      polylineRef.current = next;
      setPolylinePreview(next);
      setPolylineCursor(world);
    } else if (brush) {
      setHoverCell(null); // the gesture owns the surface now; the standalone hover hint stands down
      if (e.shiftKey) {
        // Shift = the legacy FREEHAND stroke (organic shapes a box can't make). Seed the ghost from
        // the press cell — its flags fix the whole stroke's direction (the same `strokeTarget` the
        // commit uses), so the ghost reads add-vs-remove correctly from the start. Erase mode previews
        // (and commits) the clear; the cover grade tints an add ghost to the right soft/hard fill.
        strokeRef.current = world ? [world] : [];
        if (world) {
          const press = cellAt(world);
          setTilePreview({ cells: [press], target: strokeTarget(erase ? "erase" : brush, storedCellAt(terrain, press), { tile, coverGrade, hazard }) });
        }
      } else if (world) {
        // Default = the rubber-band BOX (task 1.2): press fixes the start cell + direction, drag
        // sizes the box, release fills it. Alt forces a clear (task 1.4).
        const c = cellAt(world);
        setTileBox({ start: c, end: c, erase });
      }
    } else if (tool === "wall" && world) {
      const v = vertexAt(world);
      setWall({ from: v, to: v, erase });
    } else if (tool === "room" && world) {
      // The room box authors a room by default; Alt-held it ERASES (subtracts from rooms it covers),
      // the same Alt = erase modifier the tile box and wall run use (task 2.3). Direction-at-press.
      const c = cellAt(world);
      setRoom({ start: c, end: c, erase });
    } else if (tool === "select" && brush === null && world) {
      // Token hit-test — pure footprint-AABB math against the native pointer event
      // (the ratified interaction model, NOT a Pixi `hitArea`). A hit selects on PRESS, not the
      // trailing click — a click-and-drag of a not-yet-selected unit selects it on the way down, so
      // its range shows for the whole drag — and, if controllable/playable, arms the live drag; a hit
      // ALSO still arms the ping hold below exactly like a bare-board press (a hold over a token
      // pings; a real drag's pointer travel cancels it in `onMouseMove`, same generic tolerance check
      // — no special-casing needed).
      //
      // EXCEPT while a cell-pick throw is armed (`cellPickArmed`): an occupied cell is a legal
      // throw site (RAW puts no restriction on a Blast center),
      // so a press on a token whose clicked cell is in range must NOT claim the click for select/drag
      // at all — it stands down here so the trailing `onStageClick` sees `tokenPressRef` still false
      // and falls through to the SAME `attackArea` bare-cell commit an empty cell already reaches
      // (`onCommitCell(cellAt(world))`, using the clicked point, not the token's anchor cell — so a
      // size-2+ token's clicked cell becomes the site). An out-of-range occupied cell falls through
      // to plain select, mirroring the reach-wash convention (an illegal target still selects rather
      // than eating the click as a dead no-op).
      const hit = pickToken(combatants, world, { selectedId, cycle: false });
      const bypassSelect = hit !== null && cellPickBypassesSelect(cellPickArmed, attackRange, squareKey(cellAt(world)));
      if (hit && !bypassSelect) {
        onSelect(hit.id);
        tokenPressRef.current = true;
        // `detail` is the press's click count: only a lone press steps through a shared cell.
        const next = e.detail <= 1 ? pickToken(combatants, world, { selectedId, cycle: true }) : null;
        const p = screenPoint(e);
        if (next && next.id !== hit.id && p) cyclePressRef.current = { screenX: p.x, screenY: p.y, nextId: next.id };
        const kind = dragKind(hit);
        if (kind !== null) {
          cancelBoostDrop();
          const sq = asSquareCell(hit.pos!);
          const { minX, minY, n } = footprintBox(sq, hit.base.size);
          const startPx = minX * CELL;
          const startPy = minY * CELL;
          const dragToken = (dragTokenCounterRef.current += 1);
          latestDragTokenRef.current.set(hit.id, dragToken);
          tokenDragRef.current = {
            id: hit.id,
            pressWorld: world,
            startPx,
            startPy,
            offX: sq.x - minX,
            offY: sq.y - minY,
            lastTopLeft: { x: startPx, y: startPy },
            lastDestKey: null,
            combatant: hit,
            ghostStarted: false,
            kind,
            footprintN: n,
            dragToken,
            trail: null,
            trailRefused: false,
          };
        }
      }
      if (onPing !== undefined) {
        // Begin a ping hold: remember the press (screen px for the move-cancel test, world px for the
        // eventual ping) and arm a silent timer. A hold past `PING_HOLD_MS` fires `firePing`; any
        // travel past tolerance, the start of a drag, or an early release cancels it (clearing the
        // timer). No visual charge — the received burst is the only feedback (intentional).
        const p = screenPoint(e);
        if (p) {
          pingPressRef.current = { screenX: p.x, screenY: p.y, world, timer: setTimeout(firePing, PING_HOLD_MS) };
        }
      }
    }
  };

  // While a tile stroke is open, every move samples the pointer (`tileStrokeCommand` dedupes to the
  // cells entered, so the sample rate doesn't matter). While a wall run is open, every move re-snaps
  // the end vertex so the preview rubber-bands to the cursor.
  const onMouseMove = (e: MouseEvent) => {
    const screen = screenPoint(e);
    const world = worldPoint(screen);
    // The eraser's ring follows the pointer imperatively (no re-render per move).
    const ring = eraserRingRef.current;
    if (ring && screen) {
      ring.style.transform = `translate(${screen.x}px, ${screen.y}px)`;
      ring.hidden = false;
    }
    // Stream our live cursor to the room first, in every tool/gesture (it's a pure broadcast,
    // independent of any authoring/ping/pan in flight) — throttled in `streamCursor`. This is what
    // satisfies Flag 2 (cursor-streaming-during-a-token-drag): it runs unconditionally, before the
    // token-drag branch below even checks whether one is open, so a drag re-streams the cursor
    // exactly like a pan already does, not just while idle-hovering.
    if (world) streamCursor(world);
    // Report the token under the cursor to the renderer's raised label layer, in every tool/gesture
    // and BEFORE any of the early returns below — an armed attack or an open brush stroke returns
    // out of this handler, and a hover reveal that silently stopped working in those modes would be
    // exactly the kind of gap nobody notices. Same `hitTestToken` footprint-AABB math select and drag
    // use (`combatCells.ts`, the ratified native-DOM interaction model), coalesced to token-CHANGE
    // granularity by `hoveredTokenRef` so a move within one token costs one comparison.
    const hoveredId = world ? (hitTestToken(combatants, world)?.id ?? null) : null;
    if (hoveredId !== hoveredTokenRef.current) {
      hoveredTokenRef.current = hoveredId;
      boardHandleRef.current?.setHoveredToken(hoveredId);
      onTokenHover?.(hoveredId);
    }
    // The Measure tool follows the unit or cell under the pointer in every tool and gesture: hold-M
    // measures while anything else is armed or in flight. The tool drops repeats within one cell.
    if (world) onMeasurePointer?.({ phase: "move", end: measureEndFor(hoveredId, cellAt(world)) });
    // An open token drag owns the gesture: move the live Pixi container (EDIT) or the
    // drag-ghost (PLAY, D1/D3 — the real node never moves), recompute
    // the move-path preview only on a cell change (`lastDest`-coalescing, so a fast drag doesn't
    // recompute on every pixel of travel), and cancel any concurrent ping hold (a real drag's pointer
    // travel is what "cancels the hold" — no special-casing needed beyond this generic move).
    if (tokenDragRef.current) {
      const drag = tokenDragRef.current;
      if (world) {
        const topLeft = { x: drag.startPx + (world.x - drag.pressWorld.x), y: drag.startPy + (world.y - drag.pressWorld.y) };
        drag.lastTopLeft = topLeft;
        if (moveCommandType === "place_token") {
          boardHandleRef.current?.moveToken(drag.id, topLeft);
        } else if (!drag.ghostStarted) {
          // First real pointer travel of a PLAY drag: spin up the ghost instead of moving the real node.
          drag.ghostStarted = true;
          boardHandleRef.current?.createGhost(drag.combatant, topLeft);
        } else {
          boardHandleRef.current?.moveGhost(drag.id, topLeft);
        }
        if (moveCommandType === "move_token") {
          // The callout rides the ghost's top edge, centred: the view transform `worldPoint` inverts.
          calloutAnchorRef.current = {
            x: view.x + (topLeft.x + (drag.footprintN * CELL) / 2) * view.scale,
            y: view.y + topLeft.y * view.scale,
          };
          placeGhostCallout(calloutElRef.current, calloutAnchorRef.current);
        }
        const dest = anchorFromMinCorner({ col: Math.round(topLeft.x / CELL), row: Math.round(topLeft.y / CELL) }, { x: drag.offX, y: drag.offY });
        const key = squareKey(dest);
        if (key !== drag.lastDestKey) {
          drag.lastDestKey = key;
          const baseReach = reachableRef.current;
          let pastReach = moveCommandType === "move_token" && baseReach !== null && !baseReach.has(key);
          const trample = trampleTrail?.(drag.id, drag.trail ?? [], dest) ?? null;
          if (trample !== null) {
            // A Trampler's trail follows the pointer (Q3) and travels with the drop as its `path`.
            drag.trail = trample.trail;
            const forecast = trample.reached ? moveForecast(drag.id, dest, trample.trail) : null;
            drag.trailRefused = trailRefused(forecast);
            setMovePath({ cells: trample.cells, boostCells: new Set() });
            setMovePathRefused(drag.trailRefused);
            setTrampleRingIds(trampledIds(forecast));
            if (streamsMovePreview(drag.kind)) streamMovePreview(drag.trailRefused ? null : { cells: [...trample.cells] });
            const riders = trample.reached && !drag.trailRefused && drag.kind === "commit" ? (boostDropRiders?.(drag.id, dest, trample.trail) ?? null) : null;
            setCallout(calloutContent({ kind: drag.kind, activeUnitName, forecast, boostRiders: riders }));
            if (moveCommandType === "move_token") showDragRings({ id: drag.id, dest, legal: trample.reached && !drag.trailRefused });
          } else {
            const path = pathPreview(drag.id, dest);
            setMovePath(path);
            // A drop only a Boost reaches is past base reach by definition, whatever its anchor cell.
            if (moveCommandType === "move_token" && path !== null && path.boostCells.size > 0) pastReach = true;
            // A planning drag's trail stays local: streamed, other clients would read it as an impending move.
            if (streamsMovePreview(drag.kind)) streamMovePreview(path ? { cells: [...path.cells, ...path.boostCells] } : null);
            // The forecast (3.1), coalesced to cell changes like the path and over the same state.
            if (moveCommandType === "move_token") {
              const riders = path !== null && path.boostCells.size > 0 && drag.kind === "commit" ? (boostDropRiders?.(drag.id, dest) ?? null) : null;
              setCallout(calloutContent({ kind: drag.kind, activeUnitName, forecast: moveForecast(drag.id, dest), boostRiders: riders }));
              showDragRings({ id: drag.id, dest, legal: path !== null });
            }
          }
          setDragPastReach(pastReach);
        }
      }
      cancelPing();
      return;
    }
    // An open freehand drag: distance-sample into it (the SAME threshold test the finished
    // stroke's simplification pipeline assumes was already applied — `shouldSampleFreehandPoint`)
    // and re-derive the live preview. The polyline has no drag to sample — just track the cursor
    // for its rubber-band-to-last-vertex preview line.
    if (freehandRef.current) {
      if (world && shouldSampleFreehandPoint(freehandRef.current[freehandRef.current.length - 1], world)) {
        freehandRef.current.push(world);
        setFreehandPreview([...freehandRef.current]);
      }
      return;
    }
    // An open eraser drag: hit-test the segment TRAVELED since the last sample (D6) against every
    // stroke, accumulating every erasable id touched — every sample, not distance-thinned like
    // freehand's, because a skipped sample here is exactly the gap D6 exists to close.
    if (eraserRef.current) {
      if (world) sampleEraser(world);
      return;
    }
    if (polylineRef.current && polylineRef.current.length > 0) {
      if (world) setPolylineCursor(world);
      return;
    }
    // A ping hold is in flight: a pointer travel past the tolerance means this was a drag,
    // not a hold — cancel it. (Nothing else below applies during the hold: a ping only
    // arms in the plain select tool, so no stroke/wall/brush gesture is concurrently open.)
    if (pingPressRef.current) {
      const p = screenPoint(e);
      if (p) {
        const dx = p.x - pingPressRef.current.screenX;
        const dy = p.y - pingPressRef.current.screenY;
        if (dx * dx + dy * dy > PING_MOVE_TOLERANCE * PING_MOVE_TOLERANCE) cancelPing();
      }
      return;
    }
    if (strokeRef.current) {
      if (world) strokeRef.current.push(world);
      // Re-derive the ghost from the authoritative samples, but only re-render when the covered-cell
      // SET grows (a new cell entered). `strokeCells` is the SAME dedupe the commit uses, so the
      // ghost shows exactly the cells the stroke will paint; returning `prev` unchanged when the
      // count is steady lets React bail (no per-sample thrash). The target stays fixed at press.
      setTilePreview((prev) => {
        if (!prev || !strokeRef.current) return prev;
        const cells = strokeCells(strokeRef.current);
        return cells.length === prev.cells.length ? prev : { ...prev, cells };
      });
      return;
    }
    if (tileBoxRef.current && world) {
      // Resize the box to the cell under the cursor (its own non-listening preview layer redraws —
      // the cached terrain layer is untouched). Direction stays fixed at press (start + erase).
      const c = cellAt(world);
      const cur = tileBoxRef.current;
      if (cur.end.x !== c.x || cur.end.y !== c.y) setTileBox({ start: cur.start, end: c, erase: cur.erase });
      return;
    }
    if (wallRef.current && world) {
      setWall({ from: wallRef.current.from, to: vertexAt(world), erase: wallRef.current.erase });
      return;
    }
    if (roomRef.current && world) {
      setRoom({ start: roomRef.current.start, end: cellAt(world), erase: roomRef.current.erase });
      return;
    }
    // No gesture open: report the cell under the cursor to the Map key, in EITHER mode and with
    // nothing armed. This sits above both branches below on purpose — each of those returns early
    // once it handles its own case, so anything placed after them would silently never fire in the
    // other mode. Coalesced to cell-change granularity inside `reportHoverCell`.
    if (!world) reportHoverCell(null);
    else {
      const c = cellAt(world);
      reportHoverCell(c.x >= 0 && c.x < cols && c.y >= 0 && c.y < rows ? c : null);
    }
    // No gesture open: when an attack is armed (PLAY), track the hovered cell's affected template —
    // the red AoE preview (target + Blast/Line/Cone). Coalesced to cell-change granularity via the
    // ref so staying within one cell doesn't recompute. Independent of the brush hint below (attacks
    // arm in PLAY, brushes in EDIT — never concurrent), so the `attackRange` guard is the only gate.
    if (attackRange && world) {
      const cell = cellAt(world);
      const key = `${cell.x},${cell.y}`;
      if (key !== attackHoverCellRef.current) {
        attackHoverCellRef.current = key;
        const affected = attackAffected(cell);
        setAttackHover(affected);
        attackAffectedRef.current = affected;
        const excluded = attackLosExcluded(cell);
        setAttackLosExcludedHover(excluded);
        attackLosExcludedRef.current = excluded;
      }
      return;
    }
    // No gesture open: when a tile brush is armed, track the cell under the cursor for the hover
    // hint. Compare coords so staying within one cell doesn't re-render.
    if (brush && world) {
      const cell = cellAt(world);
      const inBounds = cell.x >= 0 && cell.x < cols && cell.y >= 0 && cell.y < rows;
      setHoverCell((prev) =>
        !inBounds ? null : prev && prev.x === cell.x && prev.y === cell.y ? prev : cell,
      );
    }
  };

  // Release over the canvas: capture the exact release point, then commit the open gesture. (An
  // off-canvas release is caught by the window-level `mouseup` net instead — see the effect above.)
  const onMouseUp = (e: MouseEvent) => {
    // PRIMARY releases only. Every gesture below was opened by a LEFT press and commits at the
    // release point, so a middle/right release has no business finishing any of them: without this
    // guard, releasing a non-primary button mid-drag commits the live gesture early, at wherever the
    // pointer happens to be. That's the symmetric half of `opensPan`'s open-gesture guard: the press
    // side declines to start a pan on top of a live gesture, and this side declines to end one. A pan's own release is unaffected — it rides the
    // WINDOW-level `mouseup` (`onPanEnd`), not this host handler.
    if (e.button !== 0) return;
    if (measureArmed) {
      const down = measurePressAtRef.current;
      measurePressAtRef.current = null;
      onMeasurePointer?.({ phase: "release", dragged: down === null || isMeasureDrag(down, { x: e.clientX, y: e.clientY }) });
      return;
    }
    if (inspectArmed) return;
    // An open token drag commits on release — "state commits on drop".
    if (tokenDragRef.current) {
      const world = worldPoint(screenPoint(e));
      if (world) {
        const drag = tokenDragRef.current;
        drag.lastTopLeft = { x: drag.startPx + (world.x - drag.pressWorld.x), y: drag.startPy + (world.y - drag.pressWorld.y) };
      }
      finishTokenDrag();
      return;
    }
    // An open freehand drag commits (RDP-simplify + one `add_annotation`) on release — the
    // polyline is NOT here; it only finishes via double-click/Enter (`onStageDblClick` /
    // the window keydown effect), never a plain mouseup.
    if (freehandRef.current) {
      const world = worldPoint(screenPoint(e));
      if (world) freehandRef.current.push(world);
      finishFreehand();
      return;
    }
    // An open eraser drag commits (one `batch` of `remove_annotation`) on release — close path 1.
    // The final segment (last sample → the release point) is hit-tested too, same as freehand's
    // final push, so the exact release location is never missed.
    if (eraserRef.current) {
      const world = worldPoint(screenPoint(e));
      if (world) sampleEraser(world);
      finishEraser();
      return;
    }
    // Released before the hold completed → no ping (a quick click, not a hold). A hold that
    // already fired cleared the ref in `firePing`, so this is a no-op then.
    if (pingPressRef.current) {
      cancelPing();
      return;
    }
    const world = worldPoint(screenPoint(e));
    if (strokeRef.current) {
      if (world) strokeRef.current.push(world);
      finishStroke();
      return;
    }
    if (tileBoxRef.current) {
      if (world) setTileBox({ start: tileBoxRef.current.start, end: cellAt(world), erase: tileBoxRef.current.erase });
      finishBox();
      return;
    }
    if (wallRef.current) {
      if (world) setWall({ from: wallRef.current.from, to: vertexAt(world), erase: wallRef.current.erase });
      finishWall();
      return;
    }
    if (roomRef.current) {
      if (world) setRoom({ start: roomRef.current.start, end: cellAt(world), erase: roomRef.current.erase });
      finishRoom();
    }
  };

  // A click on empty board (not a token — tokens stop propagation).
  // `suppressClickRef` swallows the trailing click after a genuine pan-drag, so a pan never also
  // registers as a click. A tile brush owns the click via the stroke handlers, the
  // wall tool owns it via the run handlers (a no-drag click is a zero-length run that commits
  // nothing), and the OBJECTS tool commits its whole placement
  // `batch` right here on a single click (no drag gesture of its own) — so skip all three below;
  // else an armed unit first-places; else the selection clears.
  const onStageClick = (e: MouseEvent) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    // A ping just fired on this press — the browser still delivers the trailing `click`; swallow it
    // so the ping doesn't also clear the selection (it isn't a select-clear gesture).
    if (justPingedRef.current) {
      justPingedRef.current = false;
      return;
    }
    // A token was hit on this press — swallow the trailing click: the press already selected it
    // (and possibly opened a drag), so this click must never also fall through to the
    // deselect/placement/AoE-commit paths below.
    if (tokenPressRef.current) {
      tokenPressRef.current = false;
      // A still click on the selected token of a shared cell steps to the next one down.
      const cycle = cyclePressRef.current;
      cyclePressRef.current = null;
      const p = screenPoint(e);
      if (cycle && p && (p.x - cycle.screenX) ** 2 + (p.y - cycle.screenY) ** 2 <= PING_MOVE_TOLERANCE * PING_MOVE_TOLERANCE) onSelect(cycle.nextId);
      return;
    }
    // A pending DIRECTION prompt takes precedence over every
    // other click gesture — Draw, the authoring tools, an armed AoE attack, and plain select all
    // disarm/fall away once the prompt lands (CombatApp clears `aim` itself the moment one arrives;
    // Draw/the terrain tools are independent state a GM backstop recipient could still have armed).
    // Per the "a prompt supersedes" doctrine (mirrors the modal already superseding the attacker
    // waiting-state banner), a direction click always wins here, above every other branch below —
    // any bare-cell click commits the answer (same bare-cell-only shape as {@link onCommitCell}'s
    // AoE-origin commit; a token click still falls through to select/inspect, unswallowed above).
    if (directionPicking) {
      const directionWorld = worldPoint(screenPoint(e));
      if (directionWorld) onCommitDirection(cellAt(directionWorld));
      return;
    }
    // Measure's press and release already set the ruler; a click never also deselects or commits.
    // Inspect's press already inspected the cell, and the tool is read-only.
    if (measureArmed || inspectArmed) return;
    // Draw owns every click while armed (freehand's own `onMouseUp`/`onMouseDown` already handled
    // it above; polyline's vertex was already accumulated in `onMouseDown`) — never fall through
    // to deselect/placement/AoE-commit below.
    if (drawTool) return;
    // The OBJECTS tool: one click spawns + places the configured preset (a `batch` of
    // `add_combatant`+`place_token` pairs, one per section — `objectPlacementCommand`). A `null`
    // plan (nothing picked yet) makes the click an inert no-op rather than guessing a default.
    if (tool === "object") {
      if (objectPlan) {
        const world = worldPoint(screenPoint(e));
        if (world) {
          const cmd = objectPlacementCommand({ ...objectPlan, actorId, anchor: cellAt(world) });
          if (cmd.ok) dispatch(cmd.value);
        }
      }
      return;
    }
    if (brush || tool === "wall" || tool === "room") return;
    const world = worldPoint(screenPoint(e));
    if (!world) return;

    // Annotation select: a click on a persisted
    // stroke's widened hit region selects it — pure point-to-segment math (`hitTestAnnotation`,
    // `combatDrawing.ts`), not a Pixi `hitArea`. Only live when Draw isn't armed (`annotationSelectable`)
    // — by this point brush/wall/room/object/drawTool have already returned above, so this is exactly
    // the deleted `AnnotationsLayer`'s "mirrors rooms' gate" `listening` condition.
    if (annotationSelectable) {
      const hitAnnotation = hitTestAnnotation(annotations, world);
      if (hitAnnotation) {
        selectAnnotation(hitAnnotation);
        return;
      }
    }

    // An armed AREA attack: a click on bare board commits at that cell — the AoE origin the red
    // template previews. (For an area Skirmish/Barrage weapon, a click on a unit token never reaches
    // here; it fires via `onSelect` → the parent's click-to-target, and the resolver fans the
    // template around that unit. A cell-pick throw is the deliberate exception — `cellPickArmed`
    // makes `onMouseDown`'s token-hit branch stand down for an in-range occupied cell, so THAT click
    // ALSO lands here, at the clicked cell, exactly like a bare cell — see `cellPickArmed`'s doc.)
    // This intercept is above the placement/deselect paths so an AoE aim isn't swallowed as a deselect.
    if (attackArea && !placing) {
      onCommitCell(cellAt(world));
      return;
    }

    if (!placing) {
      onSelect(null);
      deselectAnnotation(); // a plain empty-board click also drops a selected annotation
      return;
    }
    const col = Math.floor(world.x / CELL);
    const row = Math.floor(world.y / CELL);
    // Anchor at the clicked cell; the reducer is the wall (off-map / overlap → rejected, the
    // unit simply stays unplaced and undrawn). `placing` is already control-gated by the parent.
    dispatch({ type: moveCommandType, actorId, combatantId: placing.id, to: { grid: "square", x: col, y: row } });
    onSelect(placing.id);
  };

  // Double-click finishes an open polyline (alongside Enter) — but ONLY a genuine same-spot double-click, gated by `polylineRawClicksRef`'s
  // last-two-raw-clicks distance. The native `dblclick` event fires purely on the browser's own
  // timing window with no distance check, so a burst of 3+ rapid clicks (a GM placing vertices
  // quickly) fires dblclick after every click past the first, not just a real double-click. Without
  // this guard, a fast
  // 3-vertex polyline would finish after 2 clicks no matter how many more the user intended — see
  // `combatDrawing.test.ts` if that regresses (unit-testable via `polylineRawClicksRef`'s twin,
  // `accumulatePolylineVertex`'s own dedupe, is not — this is stage-local pointer-timing behavior,
  // proven instead by the `combat-annotations.e2e.ts` 3-vertex-then-Enter flow). `roomsListening`
  // (below) requires `drawTool === null`, so whenever Draw is armed (an open polyline can exist to
  // steal) a room double-click falls through to this finisher instead of stealing it.
  const onStageDblClick = (e: MouseEvent) => {
    // Clicking two ends on one unit with Measure armed is a measure, not an inspect; Inspect is read-only.
    if (measureArmed || inspectArmed) return;
    // Token dblclick-to-inspect takes priority over rooms/annotations/polyline below —
    // same native-DOM + pure-math hit-test (`hitTestToken`) `onMouseDown` uses for select/drag. This
    // fires independently of whatever `onStageClick` did with the PRECEDING click (a browser `dblclick`
    // is native double-click detection, not something our own `click` handler can suppress).
    const dblWorld = worldPoint(screenPoint(e));
    if (dblWorld) {
      const hitToken = pickToken(combatants, dblWorld, { selectedId, cycle: false });
      if (hitToken) {
        onInspect(hitToken.id);
        return;
      }
      // Annotation dblclick-to-inspect — opens the tiny recolor/delete inspector. Only
      // live when Draw isn't armed (mirrors the click-select gate above).
      if (annotationSelectable) {
        const hitAnnotation = hitTestAnnotation(annotations, dblWorld);
        if (hitAnnotation) {
          inspectAnnotation(hitAnnotation);
          return;
        }
      }
    }
    // Rooms' dblclick-to-inspect (§3 row 2) — a manual point-in-rect test
    // against the `rooms` prop, the same pure `roomContainsCell` the room-box authoring tool uses,
    // rather than Pixi's own `hitArea`/`eventMode` interactivity). Only live when `roomsListening`:
    // inert under any authoring tool so a room floor never steals a wall/tile/room gesture. GM-only
    // (`isGm`) — the room inspector's controls are GM-only (`RoomInspectorBody`'s `isGm` prop), and a
    // player's `brush`/`tool` are unconditionally `null`/`"select"` (the tool rail that would change
    // them is EDIT-only, unreachable for a player), so without the `isGm` gate the predicate would
    // be unconditionally true on a player's client and a double-click anywhere on a room floor would
    // open it. Also gated on `drawTool === null`, mirroring `annotationSelectable` above: with Draw
    // armed, a room double-click falls through to the polyline finisher below rather than always
    // winning.
    const roomsListening = isGm && drawTool === null && brush === null && tool !== "wall" && tool !== "room";
    if (roomsListening) {
      const world = worldPoint(screenPoint(e));
      if (world) {
        const cell = cellAt(world);
        const hit = Object.values(rooms).find((r) => roomContainsCell(r, cell.x, cell.y));
        if (hit) {
          onInspectRoom(hit.id);
          return; // a room dblclick never also finishes a polyline below
        }
      }
    }
    if (drawTool !== "polyline") return;
    const raw = polylineRawClicksRef.current;
    if (!raw) return;
    const [a, b] = raw;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (dx * dx + dy * dy > POLYLINE_DBLCLICK_FINISH_DIST_SQ) return; // two distinct vertices, not a finishing double-click
    finishPolyline();
  };

  // The Measure rulers (`useMeasureTool`); they change only when an end crosses a cell or unit.
  useEffect(() => {
    if (boardReady) boardHandleRef.current?.setMeasures(measureRender ?? [], { cols, rows });
  }, [boardReady, measureRender, cols, rows]);

  const onMouseLeave = useCallback(() => {
    // The pointer left the board, so nothing is hovered — drop any raised label back into the
    // ordinary layer. Refs are stable, so this needs no dependency entry.
    if (hoveredTokenRef.current !== null) {
      hoveredTokenRef.current = null;
      boardHandleRef.current?.setHoveredToken(null);
      onTokenHover?.(null);
    }
    setHoverCell(null);
    reportHoverCell(null); // the pointer left the board — the Map key has no square to describe
    setAttackHover(null); // the attack AoE preview follows the cursor; clear it off-board
    attackHoverCellRef.current = null;
    attackAffectedRef.current = null;
    setAttackLosExcludedHover(null);
    attackLosExcludedRef.current = null;
    cancelPing(); // leaving the canvas mid-hold abandons the ping hold
    streamCursor(null); // clear our cursor for everyone — the pointer left the board
    if (eraserRingRef.current) eraserRingRef.current.hidden = true;
  }, [cancelPing, reportHoverCell, streamCursor, onTokenHover]);

  // The hover hint's target: what a press on the hovered cell would do, resolved
  // against THAT cell's own flags — the hovered cell IS the would-be press cell, so its add-vs-remove
  // read matches the stroke a press would open. Only live when a tile brush is armed and not stroking.
  const hoverTarget =
    brush && hoverCell && !tilePreview
      ? strokeTarget(eraseMode ? "erase" : brush, storedCellAt(terrain, hoverCell), { tile, coverGrade, hazard })
      : null;

  // ── Wire the Pixi mount ──────────────────────────────────────────────────────────────────────
  //
  // Every native DOM handler above is mirrored into a ref right after its (re)definition — the same
  // "ref carries the latest closure" discipline `onPingRef`/`strokeCtx`/`wallCtx` already use
  // elsewhere in this file — so the ONE mount effect below can attach plain, stable listener
  // functions once and never worry about staleness.
  const onWheelRef = useRef(onWheel);
  onWheelRef.current = onWheel;
  const onMouseDownRef = useRef(onMouseDown);
  onMouseDownRef.current = onMouseDown;
  const onMouseMoveRef = useRef(onMouseMove);
  onMouseMoveRef.current = onMouseMove;
  const onMouseUpRef = useRef(onMouseUp);
  onMouseUpRef.current = onMouseUp;
  const onMouseLeaveRef = useRef(onMouseLeave);
  onMouseLeaveRef.current = onMouseLeave;
  const onStageClickRef = useRef(onStageClick);
  onStageClickRef.current = onStageClick;
  const onStageDblClickRef = useRef(onStageDblClick);
  onStageDblClickRef.current = onStageDblClick;

  // Mount once the host div first exists — gated on the SAME `size.width > 0 && size.height > 0`
  // boolean the div's own conditional render below uses (see the `hostReady` dep), rather than on
  // `size.width`/`size.height` directly, so a later resize (handled by the `handle.resize()` effect
  // above, NOT by Pixi's window-only `resizeTo`) never tears down and rebuilds the WebGL context.
  const hostReady = size.width > 0 && size.height > 0;
  // A stationary right-click over the board disarms its armed thing — the aim in any mode, Draw, a
  // terrain tool — through the Esc registry's layer 3, whatever sits above it. A right-drag is still
  // the pan. Scoped to the Pixi host, so a right-click on a window or the menu does nothing.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let down: { x: number; y: number } | null = null;
    const onDown = (e: PointerEvent) => {
      if (e.button === 2) down = { x: e.clientX, y: e.clientY };
    };
    const onUp = (e: PointerEvent) => {
      if (e.button !== 2 || !down) return;
      const still = Math.abs(e.clientX - down.x) <= RIGHT_CLICK_SLOP_PX && Math.abs(e.clientY - down.y) <= RIGHT_CLICK_SLOP_PX;
      down = null;
      if (!still) return;
      // Right-click is the board's cancel, so it never commits: a half-drawn polyline is dropped
      // before Draw disarms (the `drawTool`-change cleanup would otherwise finish it).
      dropPolyline();
      esc.dismissIn("armed");
    };
    host.addEventListener("pointerdown", onDown);
    window.addEventListener("pointerup", onUp);
    return () => {
      host.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
    };
  }, [hostReady, esc, dropPolyline]);
  // Measure on touch and pen: pointer events, since a finger drag raises no mouse events.
  useMeasureTouch({
    host: hostReady ? hostRef.current : null,
    armed: measureArmed,
    endAt: (e) => {
      const s = screenPoint(e);
      const el = hostRef.current;
      if (!s || !el || s.x < 0 || s.y < 0 || s.x > el.clientWidth || s.y > el.clientHeight) return null;
      const world = worldPoint(s);
      return world ? measureEndAt(world) : null;
    },
    onPointer: onMeasurePointer,
  });
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let cancelled = false;

    const wheel = (e: WheelEvent) => onWheelRef.current(e);
    const mousedown = (e: MouseEvent) => onMouseDownRef.current(e);
    const mousemove = (e: MouseEvent) => onMouseMoveRef.current(e);
    const mouseup = (e: MouseEvent) => onMouseUpRef.current(e);
    const mouseleave = () => onMouseLeaveRef.current();
    const click = (e: MouseEvent) => onStageClickRef.current(e);
    const dblclick = (e: MouseEvent) => onStageDblClickRef.current(e);
    // Right-drag pans, so the native context menu can't be allowed to open over the board — Chrome
    // raises `contextmenu` independently of the `mousedown` that `onMouseDown` already
    // `preventDefault`s, so it needs its own suppression. Scoped to THIS host element (the Pixi
    // canvas and nothing else), never the document: right-click stays normal everywhere else in the
    // app, including the annotation inspector and skip-animations button, which are siblings of the
    // host inside `.combat-stage-wrap` rather than children of it.
    const contextmenu = (e: MouseEvent) => e.preventDefault();
    host.addEventListener("wheel", wheel, { passive: false });
    host.addEventListener("mousedown", mousedown);
    host.addEventListener("mousemove", mousemove);
    host.addEventListener("mouseup", mouseup);
    host.addEventListener("mouseleave", mouseleave);
    host.addEventListener("click", click);
    host.addEventListener("dblclick", dblclick);
    host.addEventListener("contextmenu", contextmenu);

    // `registerNode`/`unregisterNode` are the animator's `NodeRegistry` bridge (from
    // `usePresentationDirector`, identity-stable for the life of the mounted map — same as
    // `boardHandleRef`/`setBoardReady` below, referenced directly rather than through a ref).
    // `pixiCombatBoard.ts`'s `setTokens` calls these on every token rebuild.
    void mountCombatBoard(host, { registerNode, unregisterNode }).then((handle) => {
      if (cancelled) {
        handle.destroy();
        return;
      }
      boardHandleRef.current = handle;
      // Populate the animator's live ticker handle in this SAME `.then(handle)` — from
      // here on, `pixiAnimator.ts`'s `tween()`/`shakeAndPulse` ride the real `app.ticker` instead of
      // their pre-mount `requestAnimationFrame` fallback (never actually exercised in practice: no
      // beat can fire before the first `setTokens` call registers a token, which only happens once
      // `boardReady` flips below).
      setTicker(handle.app.ticker);
      // TEST-ONLY (see `boardDebug.ts`'s `declare global`) — reads a token's LIVE container position
      // straight off the same `NodeRegistry` the animator tweens, so an e2e flow can sample it
      // mid-animation without scraping rendered pixels.
      window.__combatBoardDebug = buildBoardDebugHook({
        handle,
        getNode,
        floatLogRef,
        chainFocusLogRef,
        attackLosExcludedRef,
        attackAffectedRef,
        attackRangeRef,
        reachableRef,
        cellSightlinesRef,
        measureDebugRef: measureDebugMirror,
      });
      setBoardReady(true);
    });

    return () => {
      cancelled = true;
      host.removeEventListener("wheel", wheel);
      host.removeEventListener("mousedown", mousedown);
      host.removeEventListener("mousemove", mousemove);
      host.removeEventListener("mouseup", mouseup);
      host.removeEventListener("mouseleave", mouseleave);
      host.removeEventListener("click", click);
      host.removeEventListener("dblclick", dblclick);
      host.removeEventListener("contextmenu", contextmenu);
      boardHandleRef.current?.destroy();
      boardHandleRef.current = null;
      setTicker(null);
      delete window.__combatBoardDebug;
      setBoardReady(false);
    };
    // eslint/oxlint: intentionally re-run only when the host div itself (re)mounts, not on every
    // resize — `hostReady` is that one boolean; see the comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostReady]);

  // TEST/DIAGNOSTIC ONLY — see `__combatBoardDebug.getFloatLog`. A ref, not state: writing it must
  // never re-render, and nothing in the app reads it.
  const floatLogRef = useRef<{ text: string; at: number }[]>([]);
  // TEST/DIAGNOSTIC ONLY — see `__combatBoardDebug.getChainFocusLog`.
  const chainFocusLogRef = useRef<{ ids: string[]; at: number }[]>([]);

  useBoardSync({
    boardReady,
    boardHandleRef,
    view,
    setView,
    size,
    cols,
    rows,
    terrain,
    edges,
    ground,
    rooms,
    sitrepZones,
    areaEffects,
    combatants,
    selectedId,
    viewerSlot: actorId || null,
    tokenImageCtx,
    tokenChrome,
    outOfRangeTargetIds,
    reachable,
    boostRange: dragPastReach ? (boostRange ?? null) : null,
    sensorRange,
    threatRings: dragRings ?? threatRings ?? null,
    movePath,
    movePathRefused,
    movePreviews,
    cellSightlines,
    attackHover,
    attackLosExcludedHover,
    pendingDeploy,
    annotations: boardAnnotations,
    selectedAnnotationId,
    freehandPreview,
    polylinePreview,
    polylineCursor,
    drawColor,
    fullGrid,
    terrainMechanics,
    terrainMechanicsFocus: inspect ? `${inspect.cell.x},${inspect.cell.y}` : null,
    cursors,
    ping,
    focusPoint,
    floats,
    onFloatDone,
    floatLogRef,
    projectiles,
    onProjectileDone,
    impacts,
    onImpactDone,
    chainFocus,
    chainFocusLogRef,
  });

  useAuthoringPreviewSync({
    boardReady,
    boardHandleRef,
    tileBoxPreview,
    tilePreview,
    hoverCell,
    hoverTarget,
    wallPreview,
    roomPreview,
    brush,
    terrain,
    tile,
    coverGrade,
    hazard,
    wallHeight,
    wallCoverGrade,
    cols,
    rows,
    edges,
  });

  return (
    <div
      className={`combat-stage-wrap${canPanLeft ? " combat-stage-pan" : ""}${
        // Per-tool cursor: a precise crosshair while an authoring gesture is armed (tile
        // box/brush, wall vertex-snap, room box, the OBJECTS click-to-place, or Draw) — unless a
        // pan is taking the left button (grab wins).
        !canPanLeft && (brush !== null || tool === "wall" || tool === "room" || tool === "object" || drawTool !== null || measureArmed) ? " combat-stage-authoring" : ""
      }${
        // The same crosshair while a Skirmish/Barrage aim is live, OR a pending direction prompt has
        // armed the click-a-cell mode — both read as "click a
        // target/cell here", not select (pan still wins the left button).
        !canPanLeft && (targetPicking || directionPicking) ? " combat-stage-targeting" : ""
      }${
        // A pan-drag actually in flight, on ANY button — the closed hand wins over every cursor
        // above (last class in the sheet), and is the only way a middle/right drag can show one at
        // all, since `:active` is primary-button-only.
        panning ? " combat-stage-panning" : ""
      }${!canPanLeft && inspectArmed ? " combat-stage-inspect" : ""}`}
      // The eraser's own cursor, unless a pan has the left button (the pan classes' hand wins).
      style={drawTool === "eraser" && !canPanLeft && !panning ? { cursor: ERASER_CURSOR } : undefined}
      ref={wrapRef}
    >
      {size.width > 0 && size.height > 0 && <div ref={hostRef} className="combat-stage-pixi-host" />}
      {drawTool === "eraser" && (
        <div
          ref={eraserRingRef}
          className="combat-eraser-ring"
          data-radius-px={ERASER_HIT_RADIUS * view.scale}
          hidden
          aria-hidden
          style={{ width: 2 * ERASER_HIT_RADIUS * view.scale, height: 2 * ERASER_HIT_RADIUS * view.scale, margin: -ERASER_HIT_RADIUS * view.scale }}
        />
      )}
      {/* The select gesture's TINY INSPECTOR: recolor swatches +
          delete for a double-clicked annotation, an HTML overlay OUTSIDE the Pixi canvas so its
          buttons sit in normal tab order and are trivially `getByRole`-testable — anchored over the stroke's first
          point via the SAME view-transform math `worldPoint` inverts (`screen = view.xy +
          world*scale`). Guarded on the annotation still existing (a remote erase can drop it out
          from under an open inspector; the settle effect above clears the selection the next
          render, but this guard covers the one frame in between). */}
      {selectedAnnotationId && annotationInspectorOpen && annotations[selectedAnnotationId] && (
        <AnnotationInspector
          annotation={annotations[selectedAnnotationId]}
          view={view}
          isGm={isGm}
          actorId={actorId}
          dispatch={dispatch}
          slotNameOf={slotNameOf}
          onClose={deselectAnnotation}
        />
      )}
      {/* The drag-ghost's callout (off-turn-planning 2.4/3.2): whose turn it is on a planning drag, and
          what the move would provoke. DOM over the canvas, positioned imperatively (see `callout`). */}
      {callout && (
        <GhostDragCallout heading={callout.heading} rows={callout.rows} getAnchor={() => calloutAnchorRef.current} elRef={calloutElRef} />
      )}
      {/* Skip-animations affordance: an HTML overlay that
          appears only while the Director is draining its beat queue. Clicking it `snap`s the map to
          current — the one catch-up primitive, user-triggered — so a player never has to wait out a
          long combat sequence. An HTML button OUTSIDE the Pixi canvas so it sits in normal tab order. */}
      {/* The Inspect readout (`useInspectTool`): beside the inspected cell, never over it. */}
      {inspect && (
        <InspectReadoutFrame cell={inspect.cell} view={view} stage={size}>
          {inspect.body}
        </InspectReadoutFrame>
      )}
      {boostDrop && <BoostDropCard cell={boostDrop.dest} view={view} stage={size} riders={boostDrop.riders} onConfirm={confirmBoostDrop} onCancel={cancelBoostDrop} />}
      {isAnimating && (
        <button type="button" className="combat-skip-anim" onClick={onSkipAnimations}>
          Skip animations
        </button>
      )}
      {overlay}
    </div>
  );
}
